// Summary tile for the top task row: a label, a count that links to the list (orange count + ›),
// and a filled "+ New" button linking to the entity-creation page.
import React, { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SvgChevronRight from '@icons/chevron-right.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { formatNumber } from '@utils/utils.tsx';
import DashboardInfoTooltip from './DashboardInfoTooltip.tsx';

interface DashboardSummaryTileProps {
  label: string;
  count?: number;
  // List URL the count links to.
  listTo: string;
  // Creation URL the "+ New" button navigates to. Ignored when onCreate is provided.
  createTo?: string;
  // Alternative create action (e.g. open a sidebar) instead of navigating to createTo.
  onCreate?: () => void;
  createLabel: string;
  // Optional info tooltip beside the label (icon shown only when provided).
  tooltip?: ReactNode;
}

const DashboardSummaryTile: React.FC<DashboardSummaryTileProps> = ({ label, count, listTo, createTo, onCreate, createLabel, tooltip }) => {
  const navigate = useNavigate();
  const handleCreate = onCreate ?? (createTo ? () => navigate(createTo) : undefined);
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border border-solid border-content-quaternary rounded-lg">
      <span className="inline-flex items-center gap-1 text-sm text-content-primary">
        {label}
        <DashboardInfoTooltip content={tooltip} />
      </span>
      <div className="flex items-center gap-3 shrink-0">
        <Link to={listTo} className="inline-flex items-center gap-1 transition-colors hover:opacity-80">
          <span className="text-lg font-semibold text-content-brand">{count != null ? formatNumber(count, 0) : '—'}</span>
          <SvgChevronRight className="w-3 h-3 text-content-brand" />
        </Link>
        {handleCreate && <RingilButton mode="filled" size="small" icon={<SvgPlus className="w-3 h-3" />} label={createLabel} onClick={handleCreate} />}
      </div>
    </div>
  );
};

export default DashboardSummaryTile;